import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isActivityItem, resolveActivityType } from '../activityTypes.js';
import { playActivitySound } from '../audio/activitySfx.js';
import { playBadItemSound, playLeadHitSound, playStreamLeadHitSound } from '../audio/gameSfx.js';
import { isStreamLead } from '../systems/leadScoring.js';
import { GAME, GAME_STATUS, ITEM_TYPES, OUTCOMES } from '../config.js';
import { getDifficultySettings, getMotionSpeedMultiplier } from '../systems/difficulty.js';
import { getEnrichmentPace, hasEnrichmentProgress } from '../systems/enrichmentPace.js';
import { createInitialState, buildResult } from '../systems/gameState.js';
import { clearBackendLeadQueue, ingestBackendLeads } from '../systems/backendLeadSpawn.js';
import { updateItemsMotion } from '../systems/itemMotion.js';
import { applyItemHit, applyItemHitEffects, expireItems } from '../systems/scoring.js';
import { useGameLoop } from './useGameLoop.js';
import { useSpawner } from './useSpawner.js';

function getElapsedSeconds(startedAtMs, duration, timeLeft, useDurationLimit) {
    if (useDurationLimit) {
        return Math.max(0, Math.round(duration - timeLeft));
    }
    if (!startedAtMs) return 0;
    return Math.max(0, Math.round((Date.now() - startedAtMs) / 1000));
}

export function useLeadPopGame(options = {}) {
    const {
        duration = GAME.DEFAULT_DURATION,
        difficulty = 'normal',
        autoStart = true,
        deferAutoStart = false,
        useDurationLimit = false,
        paused = false,
        onReady,
        onStart,
        onScore,
        onItemHit,
        onLifeLost,
        onGameEnd,
        enrichmentProgress = null,
        onBadItemHit,
    } = options;

    const boardRef = useRef(null);
    const backendLeadQueueRef = useRef([]);
    const enrichmentProgressRef = useRef(enrichmentProgress);
    const finishedRef = useRef(false);
    const startedAtRef = useRef(null);
    const callbacksRef = useRef({
        onReady,
        onStart,
        onScore,
        onItemHit,
        onLifeLost,
        onGameEnd,
    });

    const [state, setState] = useState(() => createInitialState(duration, useDurationLimit));

    useEffect(() => {
        enrichmentProgressRef.current = enrichmentProgress;
    }, [enrichmentProgress]);

    const { maybeSpawn, resetSpawner } = useSpawner({
        difficulty,
        boardRef,
        enrichmentProgressRef,
        backendLeadQueueRef,
    });

    useEffect(() => {
        callbacksRef.current = {
            onReady,
            onStart,
            onScore,
            onItemHit,
            onLifeLost,
            onGameEnd,
        };
    }, [onReady, onStart, onScore, onItemHit, onLifeLost, onGameEnd]);

    useEffect(() => {
        callbacksRef.current.onReady?.();
    }, []);

    const finish = useCallback(
        (outcome = OUTCOMES.FINISHED) => {
            setState((current) => {
                if (current.status === GAME_STATUS.FINISHED) return current;

                const timeSeconds = getElapsedSeconds(
                    startedAtRef.current,
                    duration,
                    current.timeLeft,
                    useDurationLimit,
                );
                const result = buildResult(current, outcome, timeSeconds);
                finishedRef.current = true;
                callbacksRef.current.onGameEnd?.(result);

                return {
                    ...current,
                    status: GAME_STATUS.FINISHED,
                    outcome,
                };
            });
        },
        [duration, useDurationLimit],
    );

    const completeSession = useCallback(() => {
        finish(OUTCOMES.FINISHED);
    }, [finish]);

    const start = useCallback(() => {
        finishedRef.current = false;
        startedAtRef.current = Date.now();
        resetSpawner();
        setState((current) => {
            if (current.status === GAME_STATUS.RUNNING) return current;
            const next =
                current.status === GAME_STATUS.FINISHED
                    ? {
                          ...createInitialState(duration, useDurationLimit),
                          status: GAME_STATUS.RUNNING,
                      }
                    : { ...current, status: GAME_STATUS.RUNNING };
            callbacksRef.current.onStart?.();
            return next;
        });
    }, [duration, resetSpawner, useDurationLimit]);

    const pause = useCallback(() => {
        setState((current) => {
            if (current.status !== GAME_STATUS.RUNNING) return current;
            return { ...current, status: GAME_STATUS.PAUSED };
        });
    }, []);

    const resume = useCallback(() => {
        setState((current) => {
            if (current.status !== GAME_STATUS.PAUSED) return current;
            return { ...current, status: GAME_STATUS.RUNNING };
        });
    }, []);

    const skip = useCallback(() => {
        finish(OUTCOMES.SKIPPED);
    }, [finish]);

    const reset = useCallback(() => {
        finishedRef.current = false;
        startedAtRef.current = null;
        clearBackendLeadQueue(backendLeadQueueRef);
        resetSpawner();
        setState(createInitialState(duration, useDurationLimit));
    }, [duration, resetSpawner, useDurationLimit]);

    const resolveBoardSize = useCallback(() => {
        const rect = boardRef.current?.getBoundingClientRect?.();
        return {
            width: rect?.width || 1,
            height: rect?.height || 1,
        };
    }, []);

    const spawnBackendLead = useCallback(
        (reviewRow) => {
            if (!reviewRow) return 0;
            let consumed = 0;
            setState((current) => {
                const result = ingestBackendLeads(current, [reviewRow], {
                    difficulty,
                    boardSize: resolveBoardSize(),
                    queueRef: backendLeadQueueRef,
                });
                consumed = result.consumed;
                return result.state;
            });
            return consumed;
        },
        [difficulty, resolveBoardSize],
    );

    const spawnBackendLeads = useCallback(
        (reviewRows) => {
            if (!reviewRows?.length) return 0;
            let consumed = 0;
            setState((current) => {
                const result = ingestBackendLeads(current, reviewRows, {
                    difficulty,
                    boardSize: resolveBoardSize(),
                    queueRef: backendLeadQueueRef,
                });
                consumed = result.consumed;
                return result.state;
            });
            return consumed;
        },
        [difficulty, resolveBoardSize],
    );

    const finalizeHitState = useCallback(
        (current, item, next) => {
            callbacksRef.current.onItemHit?.(item);
            callbacksRef.current.onScore?.(next.score);

            if (next.lives < current.lives) {
                callbacksRef.current.onLifeLost?.(next.lives);
            }

            if (next.lives <= 0) {
                const timeSeconds = getElapsedSeconds(
                    startedAtRef.current,
                    duration,
                    next.timeLeft,
                    useDurationLimit,
                );
                const result = buildResult(next, OUTCOMES.DIED, timeSeconds);
                finishedRef.current = true;
                callbacksRef.current.onGameEnd?.(result);
                return {
                    ...next,
                    status: GAME_STATUS.FINISHED,
                    outcome: OUTCOMES.DIED,
                };
            }

            return next;
        },
        [duration, useDurationLimit],
    );

    const playItemHitSound = useCallback(
        (item) => {
            if (isActivityItem(item)) {
                playActivitySound(resolveActivityType(item));
            } else if (item.type === ITEM_TYPES.BAD_ITEM) {
                playBadItemSound();
                onBadItemHit?.();
            } else if (isStreamLead(item)) {
                playStreamLeadHitSound();
            } else if (
                item.type === ITEM_TYPES.GOOD_LEAD ||
                item.type === ITEM_TYPES.HOT_LEAD ||
                item.type === ITEM_TYPES.BONUS
            ) {
                playLeadHitSound(item.type);
            }
        },
        [onBadItemHit],
    );

    const popItemAt = useCallback(
        (item) => {
            setState((current) => {
                if (current.status !== GAME_STATUS.RUNNING) return current;
                if (!current.items.some((entry) => entry.id === item.id)) return current;

                const next = applyItemHitEffects(current, item);
                const items = current.items.map((entry) =>
                    entry.id === item.id ? { ...entry, popping: true } : entry,
                );

                return finalizeHitState(current, item, { ...next, items });
            });
        },
        [finalizeHitState],
    );

    const removePoppedItem = useCallback((itemId) => {
        setState((current) => ({
            ...current,
            items: current.items.filter((entry) => entry.id !== itemId),
        }));
    }, []);

    const hitItem = useCallback(
        (item) => {
            playItemHitSound(item);

            setState((current) => {
                if (current.status !== GAME_STATUS.RUNNING) return current;

                const next = applyItemHit(current, item);
                return finalizeHitState(current, item, next);
            });
        },
        [finalizeHitState, playItemHitSound],
    );

    useEffect(() => {
        if (autoStart && !deferAutoStart) start();
    }, [autoStart, deferAutoStart, start]);

    useEffect(() => {
        if (paused) pause();
        else if (state.status === GAME_STATUS.PAUSED) resume();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paused, pause, resume]);

    const runFrame = useCallback(
        (deltaMs) => {
            setState((current) => {
                if (current.status !== GAME_STATUS.RUNNING) return current;

                const timeLeft = useDurationLimit
                    ? Math.max(0, current.timeLeft - deltaMs / 1000)
                    : current.timeLeft;
                let next = {
                    ...current,
                    timeLeft,
                };

                next = expireItems(next, Date.now());
                const elapsedSeconds = getElapsedSeconds(
                    startedAtRef.current,
                    duration,
                    next.timeLeft,
                    useDurationLimit,
                );
                const enrichmentPace = hasEnrichmentProgress(enrichmentProgressRef.current)
                    ? getEnrichmentPace(
                          enrichmentProgressRef.current,
                          getDifficultySettings(difficulty),
                      )
                    : null;
                const motionSpeed =
                    getMotionSpeedMultiplier(enrichmentPace?.rampElapsedSeconds ?? elapsedSeconds) *
                    (enrichmentPace?.motionScale ?? 1);
                const boardRect = boardRef.current?.getBoundingClientRect?.();
                const boardSize = {
                    width: boardRect?.width || 400,
                    height: boardRect?.height || 320,
                };
                next = {
                    ...next,
                    items: updateItemsMotion(
                        next.items,
                        deltaMs,
                        undefined,
                        motionSpeed,
                        boardSize,
                    ),
                };
                next = maybeSpawn(next);

                if (useDurationLimit && timeLeft <= 0 && !finishedRef.current) {
                    const timeSeconds = getElapsedSeconds(
                        startedAtRef.current,
                        duration,
                        timeLeft,
                        useDurationLimit,
                    );
                    const result = buildResult(next, OUTCOMES.FINISHED, timeSeconds);
                    finishedRef.current = true;
                    callbacksRef.current.onGameEnd?.(result);
                    return {
                        ...next,
                        status: GAME_STATUS.FINISHED,
                        outcome: OUTCOMES.FINISHED,
                    };
                }

                return next;
            });
        },
        [difficulty, duration, maybeSpawn, useDurationLimit],
    );

    const runFrameRef = useRef(runFrame);

    useEffect(() => {
        runFrameRef.current = runFrame;
    }, [runFrame]);

    useGameLoop((deltaMs) => runFrameRef.current(deltaMs), state.status === GAME_STATUS.RUNNING);

    const api = useMemo(
        () => ({
            start,
            pause,
            resume,
            skip,
            finish,
            completeSession,
            reset,
            spawnBackendLead,
            spawnBackendLeads,
        }),
        [
            start,
            pause,
            resume,
            skip,
            finish,
            completeSession,
            reset,
            spawnBackendLead,
            spawnBackendLeads,
        ],
    );

    return {
        state,
        boardRef,
        api,
        hitItem,
        popItemAt,
        removePoppedItem,
        playItemHitSound,
        spawnBackendLead,
        spawnBackendLeads,
        useDurationLimit,
    };
}
